import { useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Check, Eye, EyeOff, LogOut, Loader2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import Dashboard from "@/pages/Dashboard";
import AdminPanel from "@/pages/AdminPanel";
import { API, authStore } from "@/apiConfig";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const PhoneInput = ({ value, onChange, testId }) => (
  <div className="flex" data-testid={testId}>
    <div className="phone-prefix">
      <span style={{ fontSize: 18 }}>🇮🇳</span>
      <span>+91</span>
    </div>
    <input
      type="tel"
      className="phone-input"
      placeholder="Enter mobile number"
      value={value}
      onChange={(e) => {
        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
        onChange(val);
      }}
      maxLength={10}
      data-testid={`${testId}-field`}
    />
  </div>
);

const PasswordInput = ({ value, onChange, placeholder, testId }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        className="auth-input pr-10"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
      <button
        type="button"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
        onClick={() => setShow(!show)}
        data-testid={`${testId}-toggle`}
        tabIndex={-1}
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
};

const SignUpForm = ({ onSuccess, defaultInviteCode }) => {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState(defaultInviteCode || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!phone || phone.length !== 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        {
          phone,
          password,
          invitation_code: inviteCode || null,
        }
      );
      authStore.setToken(data.access_token);
      toast.success("Registration successful!");
      onSuccess(data);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 animate-fade-in" data-testid="signup-form">
      <div>
        <label className="auth-label">Phone Number</label>
        <PhoneInput value={phone} onChange={setPhone} testId="signup-phone" />
      </div>

      <div>
        <label className="auth-label">Password</label>
        <PasswordInput
          value={password}
          onChange={setPassword}
          placeholder="Create password"
          testId="signup-password"
        />
      </div>

      <div>
        <label className="auth-label">
          Invitation Code <span className="optional-badge">(Optional)</span>
        </label>
        <input
          type="text"
          className="auth-input"
          placeholder="Invitation code"
          value={inviteCode}
          onChange={(e) => setInviteCode(e.target.value)}
          data-testid="signup-invite-code"
        />
      </div>

      {error && (
        <p className="error-text" data-testid="signup-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        className="btn-gold"
        disabled={loading}
        data-testid="signup-submit-btn"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 size={18} className="animate-spin" /> Registering...
          </span>
        ) : (
          "Register Now"
        )}
      </button>
    </form>
  );
};

const SignInForm = ({ onSuccess }) => {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!phone || phone.length !== 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    if (!password) {
      setError("Password is required");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { phone, password }
      );
      authStore.setToken(data.access_token);
      toast.success("Signed in successfully!");
      onSuccess(data);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 animate-fade-in" data-testid="signin-form">
      <div>
        <label className="auth-label">Phone Number</label>
        <PhoneInput value={phone} onChange={setPhone} testId="signin-phone" />
      </div>

      <div>
        <label className="auth-label">Password</label>
        <PasswordInput
          value={password}
          onChange={setPassword}
          placeholder="Enter password"
          testId="signin-password"
        />
      </div>

      {error && (
        <p className="error-text" data-testid="signin-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        className="btn-gold"
        disabled={loading}
        data-testid="signin-submit-btn"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 size={18} className="animate-spin" /> Signing In...
          </span>
        ) : (
          "Sign In"
        )}
      </button>
    </form>
  );
};

const SuccessState = ({ user, onLogout }) => (
  <div className="success-card animate-fade-in-up" data-testid="auth-success">
    <div className="success-icon">
      <Check size={28} className="text-[#d4af37]" />
    </div>
    <h3 className="heading-serif text-2xl text-white mb-2">Welcome!</h3>
    <p className="text-slate-400 text-sm mb-1">
      Signed in as <span className="text-[#d4af37] font-medium">+91 {user.phone}</span>
    </p>
    <p className="text-slate-500 text-xs mb-6">
      Role: {user.role}
    </p>
    <button
      onClick={onLogout}
      className="btn-gold flex items-center justify-center gap-2"
      data-testid="logout-btn"
    >
      <LogOut size={16} /> Sign Out
    </button>
  </div>
);

const AuthPopup = ({ onSuccess }) => {
  const [activeTab, setActiveTab] = useState("signin");
  const refCode = new URLSearchParams(window.location.search).get("ref") || "";

  return (
    <div className="auth-card p-8 w-full max-w-md animate-fade-in-up" data-testid="auth-popup">
      <h2 className="heading-serif text-2xl text-white text-center mb-6">
        {activeTab === "signin" ? "Welcome Back" : "Create Account"}
      </h2>

      <div className="auth-tabs" data-testid="auth-tabs">
        <button
          className={`auth-tab ${activeTab === "signin" ? "active" : ""}`}
          onClick={() => setActiveTab("signin")}
          data-testid="tab-signin"
        >
          Sign In
        </button>
        <button
          className={`auth-tab ${activeTab === "signup" ? "active" : ""}`}
          onClick={() => setActiveTab("signup")}
          data-testid="tab-signup"
        >
          Sign Up
        </button>
      </div>

      {activeTab === "signin" ? (
        <SignInForm onSuccess={onSuccess} />
      ) : (
        <SignUpForm onSuccess={onSuccess} defaultInviteCode={refCode} />
      )}
    </div>
  );
};

const Header = () => (
  <header
    className="fixed top-0 left-0 right-0 z-50 flex items-center justify-end px-6 py-4 sm:px-10"
    data-testid="header"
  >
    <div className="flex items-center gap-3" data-testid="logo">
      <div className="w-8 h-8 rounded-md bg-[#d4af37] flex items-center justify-center">
        <span className="text-[#050b14] font-bold text-sm heading-serif">A</span>
      </div>
      <span className="logo-text text-xl text-white">
        Amaz <span className="text-[#d4af37]">Wealth</span>
      </span>
    </div>
  </header>
);

const Home = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  // Check if already logged in
  useState(() => {
    if (!authStore.getToken()) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/auth/me`)
      .then(({ data }) => {
        setUser(data);
        setChecking(false);
      })
      .catch(() => {
        authStore.clearToken();
        setChecking(false);
      });
  }, []);

  const handleAuthSuccess = useCallback((data) => {
    setUser(data);
  }, []);

  const handleLogout = useCallback(() => {
    authStore.clearToken();
    setUser(null);
  }, []);

  const handleUserUpdate = useCallback((data) => {
    setUser(data);
  }, []);

  if (checking) {
    return (
      <div className="landing-bg">
        <div className="landing-content min-h-screen flex items-center justify-center">
          <Loader2 size={32} className="animate-spin text-[#d4af37]" />
        </div>
      </div>
    );
  }

  // Show Dashboard if logged in
  if (user) {
    return (
      <Dashboard
        user={user}
        onLogout={handleLogout}
        onUserUpdate={handleUserUpdate}
      />
    );
  }

  // Show Landing Page
  return (
    <div className="landing-bg" data-testid="landing-page">
      <div className="landing-content min-h-screen flex flex-col">
        <Header />

        <main className="flex-1 flex items-center justify-center px-4 pt-16 pb-8">
          <div className="w-full max-w-6xl flex flex-col lg:flex-row items-center justify-between gap-12">
            {/* Left: Marketing Copy */}
            <div className="flex-1 text-center lg:text-left animate-fade-in-up max-w-lg">
              <h1 className="heading-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
                Your Wealth,{" "}
                <span className="text-[#d4af37]">Elevated</span>
              </h1>
              <p className="text-slate-400 text-base sm:text-lg leading-relaxed mb-4">
                Premium wealth management solutions designed for the ambitious.
                Secure, sophisticated, and tailored to your financial vision.
              </p>
              <div className="flex items-center gap-4 justify-center lg:justify-start mt-6">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#d4af37]" />
                  Secure Investments
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#d4af37]" />
                  Expert Advisory
                </div>
              </div>
            </div>

            {/* Right: Auth Popup */}
            <div className="flex-shrink-0">
              <AuthPopup onSuccess={handleAuthSuccess} />
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="text-center py-4 text-slate-600 text-xs" data-testid="footer">
          &copy; 2026 Amaz Wealth. All rights reserved.
        </footer>
      </div>
    </div>
  );
};

function App() {
  return (
    <div>
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#0a1128",
            border: "1px solid rgba(212, 175, 55, 0.3)",
            color: "#ffffff",
          },
        }}
      />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/adminadmin" element={<AdminPanel />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
